import { useMemo, useState } from "react";
import { useStore } from "../data/store";
import { useToast } from "./Toast";
import { toFa } from "@soheil/core";

const EXERCISE_MIN = [15, 30, 45, 60];
const SLEEP_HOURS = [5, 6, 7, 8, 9];

/**
 * آب · ورزش · خواب.
 *
 * بازنویسی بعد از تست کاربر: نسخهٔ قبلی ثبت می‌کرد ولی هیچ چیزی نشان نمی‌داد،
 * پس کاربر فکر می‌کرد دکمه خراب است. حالا هر سه وضعیت امروز را نشان می‌دهند.
 */
export function Health() {
  const { state, dispatch, today } = useStore();
  const toast = useToast();
  const [picker, setPicker] = useState<"exercise" | "sleep" | null>(null);

  const goal = state.settings.waterGoal;
  const water = state.health.find((h) => h.kind === "water" && h.localDate === today)?.value ?? 0;

  const exerciseToday = useMemo(
    () => state.health.filter((h) => h.kind === "exercise" && h.localDate === today),
    [state.health, today],
  );
  const exerciseMin = exerciseToday.reduce((a, h) => a + h.value, 0);
  const sleep = state.health.find((h) => h.kind === "sleep" && h.localDate === today);

  const logExercise = (min: number) => {
    dispatch({ type: "logHealth", kind: "exercise", day: today, value: min });
    setPicker(null);
    toast(`${toFa(min)} دقیقه ورزش ثبت شد`, {
      undo: () => dispatch({ type: "removeLastExercise", day: today }),
    });
  };

  const logSleep = (hours: number) => {
    const had = !!sleep;
    dispatch({ type: "logHealth", kind: "sleep", day: today, value: hours });
    setPicker(null);
    toast(had ? `خواب به ${toFa(hours)} ساعت اصلاح شد` : `${toFa(hours)} ساعت خواب ثبت شد`);
  };

  return (
    <section className="block">
      <h2>سلامتی</h2>

      {/* آب */}
      <div className="water">
        <div className="water-head">
          <span>💧 آب</span>
          <b>{toFa(water)} از {toFa(goal)}</b>
        </div>
        <div className="cups">
          {Array.from({ length: goal }, (_, i) => (
            <button
              key={i}
              className="cup"
              data-on={i < water ? "1" : "0"}
              aria-label={`لیوان ${i + 1}`}
              onClick={() => {
                const v = i < water ? i : i + 1;
                dispatch({ type: "logHealth", kind: "water", day: today, value: v });
                if (v >= goal && water < goal) toast("هدف آب امروز کامل شد 💧");
              }}
            />
          ))}
        </div>
      </div>

      {/* ورزش */}
      <div className="hcard" data-done={exerciseMin > 0 ? "1" : "0"}>
        <button className="hcard-main" onClick={() => setPicker(picker === "exercise" ? null : "exercise")}>
          <span className="hcard-ico">🏃</span>
          <div className="hcard-txt">
            <b>ورزش</b>
            <span>{exerciseMin > 0
              ? `${toFa(exerciseMin)} دقیقه امروز${exerciseToday.length > 1 ? ` · ${toFa(exerciseToday.length)} جلسه` : ""}`
              : "هنوز ثبت نشده"}</span>
          </div>
          <span className="hcard-act">{exerciseMin > 0 ? "افزودن" : "ثبت"}</span>
        </button>

        {picker === "exercise" && (
          <div className="picker">
            {EXERCISE_MIN.map((m) => (
              <button key={m} onClick={() => logExercise(m)}>{toFa(m)} دقیقه</button>
            ))}
          </div>
        )}

        {exerciseMin > 0 && picker !== "exercise" && (
          <button
            className="hcard-undo"
            onClick={() => {
              dispatch({ type: "removeLastExercise", day: today });
              toast("آخرین ورزش حذف شد", { tone: "info" });
            }}
          >
            حذف آخرین ثبت
          </button>
        )}
      </div>

      {/* خواب */}
      <div className="hcard" data-done={sleep ? "1" : "0"}>
        <button className="hcard-main" onClick={() => setPicker(picker === "sleep" ? null : "sleep")}>
          <span className="hcard-ico">🌙</span>
          <div className="hcard-txt">
            <b>خواب</b>
            <span>{sleep ? `${toFa(sleep.value)} ساعت` : "هنوز ثبت نشده"}</span>
          </div>
          <span className="hcard-act">{sleep ? "تغییر" : "ثبت"}</span>
        </button>

        {picker === "sleep" && (
          <div className="picker">
            {SLEEP_HOURS.map((h) => (
              <button key={h} data-on={sleep?.value === h ? "1" : "0"} onClick={() => logSleep(h)}>
                {toFa(h)} ساعت
              </button>
            ))}
          </div>
        )}

        {sleep && picker !== "sleep" && (
          <button
            className="hcard-undo"
            onClick={() => {
              dispatch({ type: "removeHealth", id: sleep.id });
              toast("ثبت خواب حذف شد", { tone: "info" });
            }}
          >
            حذف ثبت خواب
          </button>
        )}
      </div>
    </section>
  );
}
